import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  alpha,
} from '@mui/material';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LinkIcon from '@mui/icons-material/Link';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
}

// Default colors for non-POS contexts
const defaultColors: POSColors = {
  bg: '#F8FAFC',
  surface: '#FFFFFF',
  surfaceLight: '#F1F5F9',
  border: '#E2E8F0',
  textPrimary: '#1E293B',
  textSecondary: '#475569',
  textMuted: '#94A3B8',
  accent: '#2563EB',
  accentLight: '#3B82F6',
};

interface BarcodeNotFoundDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** The barcode that was not found */
  barcode: string;
  /** Callback when user selects "Link to existing product" */
  onLinkToExisting: (barcode: string) => void;
  /** Callback when user selects "Create new product" */
  onCreateNew: (barcode: string) => void;
  /** Optional theme colors (for POS context) */
  colors?: POSColors;
}

/**
 * Dialog displayed when a scanned barcode doesn't match any existing product.
 * Presents two options:
 * 1. Link the barcode to an existing product
 * 2. Create a new product with the barcode pre-populated
 */
export const BarcodeNotFoundDialog: React.FC<BarcodeNotFoundDialogProps> = ({
  open,
  onClose,
  barcode,
  onLinkToExisting,
  onCreateNew,
  colors = defaultColors,
}) => {
  const handleLinkClick = () => {
    onLinkToExisting(barcode);
  };

  const handleCreateClick = () => {
    onCreateNew(barcode);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            backgroundColor: alpha('#F59E0B', 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <QrCodeIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
        </Box>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            color: colors.textPrimary,
          }}
        >
          Code-barres non trouvé
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            sx={{
              color: colors.textSecondary,
              fontSize: '14px',
              lineHeight: 1.6,
            }}
          >
            Le code-barres scanné n'est associé à aucun produit existant.
          </Typography>

          {/* Display the scanned barcode */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 1.5,
              px: 2,
              backgroundColor: colors.surfaceLight,
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: '18px',
                fontWeight: 600,
                color: colors.textPrimary,
                letterSpacing: '0.05em',
              }}
            >
              {barcode}
            </Typography>
          </Box>

          <Typography
            sx={{
              color: colors.textSecondary,
              fontSize: '14px',
              lineHeight: 1.6,
            }}
          >
            Que souhaitez-vous faire ?
          </Typography>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Button
              variant="outlined"
              onClick={handleLinkClick}
              startIcon={<LinkIcon />}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                py: 1.5,
                px: 2,
                borderColor: colors.border,
                color: colors.textPrimary,
                backgroundColor: colors.surface,
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  borderColor: colors.accent,
                  backgroundColor: alpha(colors.accent, 0.05),
                },
              }}
            >
              <Box sx={{ textAlign: 'left', ml: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                  Associer à un produit existant
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: colors.textMuted,
                    fontWeight: 400,
                  }}
                >
                  Rechercher et lier ce code-barres à un produit
                </Typography>
              </Box>
            </Button>

            <Button
              variant="outlined"
              onClick={handleCreateClick}
              startIcon={<AddCircleOutlineIcon />}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                py: 1.5,
                px: 2,
                borderColor: colors.accent,
                color: colors.accent,
                backgroundColor: alpha(colors.accent, 0.05),
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  borderColor: colors.accentLight,
                  backgroundColor: alpha(colors.accent, 0.1),
                },
              }}
            >
              <Box sx={{ textAlign: 'left', ml: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                  Créer un nouveau produit
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: colors.textMuted,
                    fontWeight: 400,
                  }}
                >
                  Ajouter un nouveau produit avec ce code-barres
                </Typography>
              </Box>
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="text"
          sx={{
            color: colors.textMuted,
            '&:hover': {
              backgroundColor: alpha(colors.textMuted, 0.1),
            },
          }}
        >
          Annuler
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodeNotFoundDialog;
